/**
 * V180.5: Hook for legal consent - production-ready implementation
 *
 * Provides:
 * - Status checking (has user accepted all docs?)
 * - Recording function (save acceptances to DB)
 * - Loading states
 * - Active documents list
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import {
  getActiveLegalDocuments,
  hasAcceptedAllLegal,
  recordLegalAcceptances,
  LegalDocument,
} from '@/services/LegalService';

type LegalConsentState = {
  loading: boolean;
  saving: boolean;
  hasAcceptedAll: boolean;
  activeDocs: LegalDocument[];
};

export function useLegalConsent(language: 'en' | 'he' = 'en') {
  const { user } = useAuth();
  const userId = user?.id;

  const [state, setState] = useState<LegalConsentState>({
    loading: true,
    saving: false,
    hasAcceptedAll: false,
    activeDocs: [],
  });

  // Load legal docs + acceptance status
  useEffect(() => {
    if (!userId) {
      setState((prev) => ({ ...prev, loading: false, hasAcceptedAll: false }));
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setState((prev) => ({ ...prev, loading: true }));
        const result = await hasAcceptedAllLegal({ userId, language });
        if (cancelled) return;

        setState((prev) => ({
          ...prev,
          loading: false,
          hasAcceptedAll: result.hasAll,
          activeDocs: result.activeDocs,
        }));
      } catch (err) {
        console.error('V180.5 – useLegalConsent init failed', err);
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            hasAcceptedAll: false,
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, language]);

  const acceptAll = useCallback(
    async (clientIp?: string | null) => {
      console.log('=== V180.5 DEBUG: acceptAll() CALLED ===');
      console.log('V180.5 DEBUG: userId:', userId);
      console.log('V180.5 DEBUG: state.activeDocs:', state.activeDocs);

      if (!userId || !state.activeDocs.length) {
        console.log('V180.5 DEBUG: ABORT - no userId or no activeDocs');
        return;
      }

      setState((prev) => ({ ...prev, saving: true }));
      try {
        console.log('V180.5 DEBUG: Calling recordLegalAcceptances...');
        const result = await recordLegalAcceptances({
          userId,
          documents: state.activeDocs,
          clientIp: clientIp ?? null,
          userAgent:
            typeof window !== 'undefined'
              ? window.navigator.userAgent
              : null,
        });
        console.log('V180.5 DEBUG: recordLegalAcceptances result:', result);

        setState((prev) => ({
          ...prev,
          saving: false,
          hasAcceptedAll: true,
        }));
        console.log('✅ V180.5 DEBUG: acceptAll() completed successfully');
      } catch (err) {
        console.error('❌ V180.5 – acceptAll failed', err);
        setState((prev) => ({ ...prev, saving: false }));
        throw err;
      }
    },
    [userId, state.activeDocs],
  );

  return {
    ...state,
    acceptAll,
  };
}

